import {IconButton} from '@wandb/components';
import React from 'react';
import {Heading} from 'react-aria-components';
import {twMerge} from 'tailwind-merge';

import {TailwindContents} from '../../components/Tailwind';

/**
 * Shared header chrome for a drawer: a titled top bar with a close button,
 * above arbitrary content. Lives in weave/common so both App and Weave code
 * can use it.
 *
 * Decoupled from any drawer context on purpose -- the caller supplies `onClose`
 * (e.g. wired to a workspace-drawer context in app, or a local close handler in
 * weave).
 */
export const DrawerWrapper = ({
  title,
  onClose,
  children,
  fillHeight = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  // Stretch to the parent's full height and let `children` flex to fill the
  // space below the header. Use for full-height content (e.g. a detail view);
  // leave off for a content-height menu list.
  fillHeight?: boolean;
}) => {
  return (
    <TailwindContents>
      <div
        className={twMerge('flex flex-col', fillHeight && 'h-full')}
        data-test="workspace-settings-menu">
        <div
          className={twMerge(
            'flex flex-none items-center justify-between',
            'px-20 py-16',
            'border-b-[1px] border-solid border-wb-border'
          )}>
          {/* react-aria Heading with slot="title" so a parent react-aria
              Dialog (e.g. Dialog.Drawer) uses this as its accessible name; we
              keep the existing styling via className.
              TODO: aria's Heading defaults to level 3, but the wrapper
              previously used <h1>, so we preserve that behavior with level={1}.
              If we decide to change the level, we'll need to update integration
              tests.
              TODO: switch to Dialog.Heading from @wandb/components once its
              typography can be made to match this. */}
          <Heading
            slot="title"
            level={1}
            className={twMerge('text-xl font-semibold leading-[32px]')}>
            {title}
          </Heading>
          <IconButton
            variant="ghost"
            icon="close"
            onClick={onClose}
            aria-label="Close drawer"
          />
        </div>
        {fillHeight ? (
          <div className="min-h-0 flex-1">{children}</div>
        ) : (
          children
        )}
      </div>
    </TailwindContents>
  );
};
